import React, { useMemo, useState, useEffect } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';

// Contexts
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
// Utils
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { LISTING_STATE_PENDING_APPROVAL, LISTING_STATE_CLOSED, propTypes } from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
  parse,
  stringify,
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
} from '../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors.js';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers.js';
import { requireListingImage } from '../../util/configHelpers';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import {
  getFirstAvailableSizeOption,
  getListingSizeChoices,
  normalizeSizeValue,
  resolveSelectedSizeOption,
  toSizeOption,
} from '../../util/sizeHelpers';
import { richText } from '../../util/richText';
import { getPayoutFlowState } from '../../util/payoutFlow';
import * as listingVisibility from '../../util/listingVisibility';
import { resolveMaxQuantityForListing } from '../../util/cartHelpers';
import {
  OFFER,
  REQUEST,
  isBookingProcess,
  isNegotiationProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

// Global ducks (for Redux actions and thunks)
import { getListingsById, getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';
import { addItemToCart, removeItemFromCart } from '../../ducks/cart.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { isPhoneVerifiedForUser } from '../../util/phoneNumber';

// Shared components
import {
  H4,
  H3,
  IconSpinner,
  ListingCard,
  Page,
  NamedLink,
  NamedRedirect,
  OrderPanel,
  LayoutSingleColumn,
} from '../../components';

// Related components and modules
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import {
  createSimilarListingsFilterKey,
  fetchSimilarListings,
  getSimilarListingFilterValues,
  sendInquiry,
  setInitialValues,
  fetchTimeSlots,
  fetchTransactionLineItems,
} from './ListingPage.duck';
import { useLazySimilarListings } from './useLazySimilarListings';

import {
  LoadingPage,
  ErrorPage,
  priceData,
  listingImages,
  handleContactUser,
  handleSubmitInquiry,
  handleNavigateToMakeOfferPage,
  handleNavigateToRequestQuotePage,
  handleSubmit,
  priceForSchemaMaybe,
  productBrandSchemaMaybe,
} from './ListingPage.shared';
import { useOptimisticCartState } from './useOptimisticCartState';
import SectionHero from './SectionHero';
import SectionTextMaybe from './SectionTextMaybe';
import SectionReviews from './SectionReviews';
import SectionAuthorMaybe from './SectionAuthorMaybe';
import SectionMapMaybe from './SectionMapMaybe';
import CustomListingFields from './CustomListingFields';
import ActionBarMaybe from './ActionBarMaybe';
import SectionTags from './SectionTags';
import IconBagFilled from '../../components/IconBagFilled/IconBagFilled.js';

import css from './ListingPage.module.css';
import { useTrackViewedProduct } from '../../util/klaviyo';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;
const DEBUG_PRIVATE_LISTINGS =
  typeof process !== 'undefined' && process.env.DEBUG_PRIVATE_LISTINGS === 'true';

const { UUID } = sdkTypes;
const {
  canDirectlyTransactOnListing,
  canDirectlyViewListing,
  filterPublicListings,
  isVacationModeListing,
  isPrivateListing,
} = listingVisibility;

export const ListingPageComponent = props => {
  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === props.params.id
  );
  const [inquiryType, setInquiryType] = useState('inquiry');
  const [phoneVerificationRequired, setPhoneVerificationRequired] = useState(
    Boolean(
      props.inquiryModalOpenForListingId === props.params.id &&
        props.currentUser &&
        !isPhoneVerifiedForUser(props.currentUser)
    )
  );
  const [phoneVerificationSuccess, setPhoneVerificationSuccess] = useState(false);
  const [imageCarouselOpen, setImageCarouselOpen] = useState(false);
  const [selectedSize, setSelectedSize] = useState('');

  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    reviews = [],
    fetchReviewsError,
    sendInquiryInProgress,
    sendInquiryError,
    history,
    callSetInitialValues,
    onSendInquiry,
    onRefreshCurrentUser,
    onInitializeCardPaymentData,
    onAddToCart,
    onRemoveFromCart,
    cartItems,
    config,
    routeConfiguration,
    showOwnListingsOnly,
    saveCountBySeller,
    variantListings = [],
    similarListings = [],
    fetchSimilarListingsInProgress,
    onFetchSimilarListings,
    ...restOfProps
  } = props;

  const listingConfig = config.listing;
  const listingId = new UUID(rawParams.id);
  const isVariant = rawParams.variant != null;
  const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;
  const currentListing =
    isPendingApprovalVariant || isDraftVariant || showOwnListingsOnly
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));
  const routeListingUuid = currentListing?.id?.uuid;
  useTrackViewedProduct(currentListing);
  const baseListingPublicData = currentListing?.attributes?.publicData || {};
  const publicDataForSimilar = baseListingPublicData;
  const variantCandidates = useMemo(() => {
    const seenListingIds = new Set();
    const listings = [];

    const maybePushListing = listing => {
      const id = listing?.id?.uuid;
      if (!id || seenListingIds.has(id)) {
        return;
      }

      seenListingIds.add(id);
      listings.push(listing);
    };

    maybePushListing(currentListing);
    variantListings.forEach(maybePushListing);

    return listings;
  }, [currentListing, variantListings]);
  const sizeChoicesWithListing = useMemo(() => {
    const choiceBySize = new Map();

    variantCandidates.forEach(listing => {
      const variantListingId = listing?.id?.uuid;
      if (!variantListingId) {
        return;
      }

      const publicData = listing?.attributes?.publicData || {};
      const sizeOption = toSizeOption(
        publicData?.sizeOption || publicData?.size || publicData?.sizeLabel
      );
      const normalizedSizeValue = normalizeSizeValue(sizeOption?.values);
      if (!normalizedSizeValue) {
        return;
      }

      const rawStock = Number.parseInt(listing?.currentStock?.attributes?.quantity, 10);
      const stock = Number.isFinite(rawStock) ? rawStock : null;
      const nextChoice = {
        values: normalizedSizeValue,
        label: sizeOption?.label || normalizedSizeValue,
        listingId: variantListingId,
        stock,
        isChildVariant: !!publicData?.parentId,
      };

      const existingChoice = choiceBySize.get(normalizedSizeValue);
      if (!existingChoice) {
        choiceBySize.set(normalizedSizeValue, nextChoice);
        return;
      }

      const nextInStock = nextChoice.stock == null || nextChoice.stock > 0;
      const existingInStock = existingChoice.stock == null || existingChoice.stock > 0;
      if (nextInStock && !existingInStock) {
        choiceBySize.set(normalizedSizeValue, nextChoice);
      } else if (nextChoice.isChildVariant && !existingChoice.isChildVariant) {
        choiceBySize.set(normalizedSizeValue, nextChoice);
      }
    });

    if (choiceBySize.size > 0) {
      return Array.from(choiceBySize.values());
    }

    return getListingSizeChoices(baseListingPublicData)
      .map(option => ({
        ...option,
        listingId: routeListingUuid || null,
        stock: null,
      }))
      .filter(option => !!option?.values);
  }, [variantCandidates, baseListingPublicData, routeListingUuid]);
  const defaultSizeOption = useMemo(() => {
    if (!sizeChoicesWithListing.length) {
      return getFirstAvailableSizeOption(baseListingPublicData);
    }
    const inStockChoice =
      sizeChoicesWithListing.find(choice => choice?.stock == null || choice.stock > 0) || null;
    return inStockChoice || sizeChoicesWithListing[0];
  }, [sizeChoicesWithListing, baseListingPublicData]);
  const selectedSizeOption = useMemo(() => {
    if (!sizeChoicesWithListing.length) {
      return selectedSize
        ? resolveSelectedSizeOption(baseListingPublicData, selectedSize)
        : defaultSizeOption;
    }

    const normalizedSelectedSize = normalizeSizeValue(selectedSize);
    if (!normalizedSelectedSize) {
      return defaultSizeOption || sizeChoicesWithListing[0];
    }

    return (
      sizeChoicesWithListing.find(
        option =>
          normalizeSizeValue(option?.values) === normalizedSelectedSize ||
          normalizeSizeValue(option?.label) === normalizedSelectedSize
      ) ||
      defaultSizeOption ||
      sizeChoicesWithListing[0]
    );
  }, [sizeChoicesWithListing, selectedSize, baseListingPublicData, defaultSizeOption]);
  const activeListingId = selectedSizeOption?.listingId || routeListingUuid || null;
  const { isListingInCart, isCartActionPending, runOptimisticCartAction } = useOptimisticCartState(
    cartItems
  );
  const isInCart = isListingInCart(activeListingId);
  const isActiveListingCartActionPending = isCartActionPending(activeListingId);
  const activeListing = useMemo(() => {
    if (!activeListingId) {
      return currentListing;
    }
    return (
      variantCandidates.find(listing => listing?.id?.uuid === activeListingId) || currentListing
    );
  }, [activeListingId, currentListing, variantCandidates]);
  const isVacationMode = isVacationModeListing(activeListing);
  const vacationModeListingIds = useMemo(
    () =>
      Array.from(
        new Set(
          [currentListing, activeListing]
            .filter(listing => isVacationModeListing(listing))
            .map(listing => listing?.id?.uuid)
            .filter(Boolean)
        )
      ),
    [activeListing, currentListing]
  );
  const vacationModeCartListingIds = useMemo(
    () => vacationModeListingIds.filter(listingId => Boolean(cartItems?.[listingId])),
    [cartItems, vacationModeListingIds]
  );

  useEffect(() => {
    setSelectedSize('');
    setPhoneVerificationRequired(false);
    setPhoneVerificationSuccess(false);
  }, [routeListingUuid]);

  useEffect(() => {
    if (inquiryModalOpen && currentUser && inquiryType === 'inquiry') {
      setPhoneVerificationRequired(!isPhoneVerifiedForUser(currentUser));
    }
  }, [currentUser, inquiryModalOpen, inquiryType]);

  useEffect(() => {
    if (!vacationModeCartListingIds.length) {
      return;
    }

    vacationModeCartListingIds.forEach(listingId => {
      onRemoveFromCart(listingId);
    });
  }, [onRemoveFromCart, vacationModeCartListingIds]);

  const { brandValues, sizeValues } = useMemo(
    () => getSimilarListingFilterValues(publicDataForSimilar),
    [publicDataForSimilar]
  );
  const similarFilterKey = useMemo(
    () => createSimilarListingsFilterKey({ brandValues, sizeValues }),
    [brandValues, sizeValues]
  );
  const { hasSimilarFilters, hasIntersected, similarListingsObserverRef } = useLazySimilarListings({
    listingUuid: routeListingUuid,
    brandValues,
    sizeValues,
    similarFilterKey,
    config,
    onFetchSimilarListings,
  });

  const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
  const params = { slug: listingSlug, ...rawParams };

  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = isDraftVariant ? 'photos' : 'details';

  const isApproved =
    currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;

  // If a /pending-approval URL is shared, the UI requires
  // authentication and attempts to fetch the listing from own
  // listings. This will fail with 403 Forbidden if the author is
  // another user. We use this information to try to fetch the
  // public listing.
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

  if (shouldShowPublicListingPage) {
    return <NamedRedirect name="ListingPage" params={params} search={location.search} />;
  }

  const topbar = <TopbarContainer />;

  if (showListingError && showListingError.status === 404) {
    // 404 listing not found
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (showListingError) {
    // Other error in fetching listing
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (!currentListing.id) {
    // Still loading the listing
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  const viewerUserId = currentUser?.id?.uuid || null;
  const canViewActiveListing = canDirectlyViewListing(activeListing, viewerUserId);
  const canTransactOnActiveListing = canDirectlyTransactOnListing(activeListing, viewerUserId);
  const activeListingPrivate = isPrivateListing(activeListing);

  if (DEBUG_PRIVATE_LISTINGS) {
    // eslint-disable-next-line no-console
    console.log('[private-listings] listing-page-guard', {
      viewerId: viewerUserId,
      listingId: activeListing?.id?.uuid || activeListing?.id || null,
      isPrivate: activeListingPrivate,
      canView: canViewActiveListing,
      canTransact: canTransactOnActiveListing,
    });
  }

  if (!canViewActiveListing) {
    return <NotFoundPage staticContext={props.staticContext} />;
  }

  const {
    description = '',
    geolocation = null,
    price = null,
    title = '',
    publicData = {},
    metadata = {},
  } = activeListing.attributes;

  const richTitle = (
    <span>
      {richText(title, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
        longWordClass: css.longWord,
      })}
    </span>
  );

  const authorAvailable = activeListing && activeListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable && activeListing.author.id.uuid === currentUser.id.uuid;

  const { listingType, transactionProcessAlias, unitType } = publicData;
  if (!(listingType && transactionProcessAlias && unitType)) {
    // Listing should always contain listingType, transactionProcessAlias and unitType)
    return (
      <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} invalidListing />
    );
  }
  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const isBooking = isBookingProcess(processName);
  const isPurchase = isPurchaseProcess(processName);
  const isNegotiation = isNegotiationProcess(processName);
  const processType = isBooking
    ? 'booking'
    : isPurchase
    ? 'purchase'
    : isNegotiation
    ? 'negotiation'
    : 'inquiry';

  const validListingTypes = listingConfig.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showListingImage = requireListingImage(foundListingTypeConfig);

  const currentAuthor = authorAvailable ? activeListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);
  const authorNeedsPayoutDetails =
    ['booking', 'purchase'].includes(processType) || (isNegotiation && unitType === OFFER);
  const currentUserPayoutFlow = getPayoutFlowState(currentUser);
  const noPayoutDetailsSetWithOwnListing =
    isOwnListing && authorNeedsPayoutDetails && currentUserPayoutFlow.shouldRequirePayoutOnboarding;
  const payoutDetailsWarning = noPayoutDetailsSetWithOwnListing ? (
    <span className={css.payoutDetailsWarning}>
      <FormattedMessage id="ListingPage.payoutDetailsWarning" values={{ processType }} />
      <NamedLink name="StripePayoutPage">
        <FormattedMessage id="ListingPage.payoutDetailsWarningLink" />
      </NamedLink>
    </span>
  ) : null;

  // When user is banned or deleted the listing is also deleted.
  // Because listing can be never showed with banned or deleted user we don't have to provide
  // banned or deleted display names for the function
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');

  const { formattedPrice } = priceData(price, config.currency, intl);

  const commonParams = { params, history, routes: routeConfiguration };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    location,
    setInitialValues,
    setInquiryModalOpen,
    setInquiryType,
    setPhoneVerificationRequired,
    setPhoneVerificationSuccess,
  });
  // Note: this is for inquire transition to inquiry state in booking, purchase and negotiation processes.
  // Inquiry process is handled through handleSubmit.
  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
  });
  // This is to navigate to MakeOfferPage when NegotiationForm is submitted
  const onNavigateToMakeOfferPage = handleNavigateToMakeOfferPage({
    ...commonParams,
    getListing,
  });
  const onNavigateToRequestQuotePage = handleNavigateToRequestQuotePage({
    ...commonParams,
    getListing,
  });
  const onSubmit = handleSubmit({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    getListing,
    onInitializeCardPaymentData,
  });
  const purchaseCtaMessageId = isInCart ? 'ListingPage.removeFromCart' : 'ListingPage.addToCart';

  const closeOrderModalIfOpen = () => {
    const { pathname, search, state } = location;
    const { orderOpen, ...searchParams } = parse(search);

    if (!orderOpen) {
      return null;
    }

    const nextSearch = stringify(searchParams);
    history.push(`${pathname}${nextSearch ? `?${nextSearch}` : ''}`, state);
    return null;
  };

  const handleRemoveFromCart = () => {
    if (!activeListingId) {
      return null;
    }

    return runOptimisticCartAction(activeListingId, false, () =>
      Promise.resolve(onRemoveFromCart(activeListingId)).then(() => closeOrderModalIfOpen())
    );
  };

  const handleOrderSubmit = values => {
    if (isVacationMode) {
      return null;
    }

    if (isActiveListingCartActionPending) {
      return null;
    }

    if (isPurchase && isInCart) {
      return handleRemoveFromCart();
    }

    if (isPurchase) {
      if (!activeListingId || currentStock <= 0) {
        return;
      }

      const selectedSizeValue = normalizeSizeValue(
        values?.selectedSize || selectedSizeOption?.values
      );
      const resolvedSelectedSizeOption =
        sizeChoicesWithListing.find(
          option => normalizeSizeValue(option?.values) === selectedSizeValue
        ) ||
        resolveSelectedSizeOption(activeListing?.attributes?.publicData || {}, selectedSizeValue) ||
        null;
      const quantity = Number.parseInt(values?.quantity, 10);
      const maxQuantity = resolveMaxQuantityForListing(
        activeListing,
        resolvedSelectedSizeOption?.values || null
      );

      return runOptimisticCartAction(activeListingId, true, () =>
        Promise.resolve(
          onAddToCart({
            listingId: activeListingId,
            quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
            sellerId: currentAuthor?.id?.uuid,
            maxQuantity,
            selectedSize: resolvedSelectedSizeOption?.values || null,
            selectedSizeLabel: resolvedSelectedSizeOption?.label || null,
          })
        ).then(() => {
          return closeOrderModalIfOpen();
        })
      );
    }

    if (!canTransactOnActiveListing) {
      return;
    }

    const isCurrentlyClosed = activeListing.attributes.state === LISTING_STATE_CLOSED;
    if (isOwnListing || isCurrentlyClosed) {
      window.scrollTo(0, 0);
    } else if (isNegotiation && unitType === REQUEST) {
      onNavigateToMakeOfferPage(values);
    } else if (isNegotiation && unitType === OFFER) {
      onNavigateToRequestQuotePage(values);
    } else {
      onSubmit({ listingId: activeListingId, ...values });
    }
  };

  const facebookImages = listingImages(activeListing, 'facebook');
  const twitterImages = listingImages(activeListing, 'twitter');
  const schemaImages = listingImages(
    activeListing,
    `${config.layout.listingImage.variantPrefix}-2x`
  ).map(img => img.url);
  const marketplaceName = config.marketplaceName;
  const schemaTitle = intl.formatMessage(
    { id: 'ListingPage.schemaTitle' },
    { title, price: formattedPrice, marketplaceName }
  );
  // You could add reviews, sku, etc. into page schema
  // Read more about product schema
  // https://developers.google.com/search/docs/advanced/structured-data/product
  const productURL = `${config.marketplaceRootURL}${location.pathname}${location.search}${location.hash}`;
  const currentStock = activeListing.currentStock?.attributes?.quantity || 0;
  const schemaAvailability = !activeListing.currentStock
    ? null
    : currentStock > 0
    ? 'https://schema.org/InStock'
    : 'https://schema.org/OutOfStock';

  const availabilityMaybe = schemaAvailability ? { availability: schemaAvailability } : {};
  const noIndexMaybe =
    activeListing.attributes.state === LISTING_STATE_CLOSED ? { noIndex: true } : {};

  const handleViewPhotosClick = e => {
    // Stop event from bubbling up to prevent image click handler
    // trying to open the carousel as well.
    e.stopPropagation();
    setImageCarouselOpen(true);
  };

  const actionBar =
    mounted && currentListing.id && isOwnListing ? (
      <>
        {noPayoutDetailsSetWithOwnListing ? (
          <ActionBarMaybe
            className={classNames(css.actionBarForHeroLayout, {
              [css.actionBarNoBorderRadiusOnMobile]: !showListingImage,
            })}
            isOwnListing={isOwnListing}
            listing={currentListing}
            showNoPayoutDetailsSet={noPayoutDetailsSetWithOwnListing}
            currentUser={currentUser}
          />
        ) : null}
        <ActionBarMaybe
          className={classNames(css.actionBarForHeroLayout, {
            [css.actionBarNoBorderRadiusOnMobile]: !showListingImage,
          })}
          isOwnListing={isOwnListing}
          listing={currentListing}
          currentUser={currentUser}
          editParams={{
            id: listingId.uuid,
            slug: listingSlug,
            type: listingPathParamType,
            tab: listingTab,
          }}
        />
      </>
    ) : null;
  const hasSimilarListings = (similarListings || []).length > 0;
  const showSimilarListingsSection =
    hasSimilarFilters && hasIntersected && (fetchSimilarListingsInProgress || hasSimilarListings);

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      description={description}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      {...noIndexMaybe}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'Product',
        description: description,
        name: schemaTitle,
        image: schemaImages,
        ...productBrandSchemaMaybe(publicData),
        offers: {
          '@type': 'Offer',
          url: productURL,
          ...priceForSchemaMaybe(price),
          ...availabilityMaybe,
        },
      }}
    >
      <LayoutSingleColumn className={css.pageRoot} topbar={topbar} footer={<FooterContainer />}>
        {showListingImage ? (
          <SectionHero
            title={title}
            listing={activeListing}
            isOwnListing={isOwnListing}
            imageCarouselOpen={imageCarouselOpen}
            onImageCarouselClose={() => setImageCarouselOpen(false)}
            handleViewPhotosClick={handleViewPhotosClick}
            onManageDisableScrolling={onManageDisableScrolling}
            actionBar={actionBar}
          />
        ) : (
          isOwnListing && <div className={css.actionBarContainerForNoListingImage}>{actionBar}</div>
        )}
        <div className={css.contentWrapperForHeroLayout}>
          <div className={css.mainColumnForHeroLayout}>
            <div className={showListingImage ? css.mobileHeading : css.noListingImageHeadingHero}>
              {showListingImage ? (
                // add css logic here that applies larger margin on mobile view to push down title
                <H4 as="h1" className={css.orderPanelTitle}>
                  <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                </H4>
              ) : (
                <H3
                  as="h1"
                  className={classNames(css.orderPanelTitle, {
                    [css.titleMarginForOwnListingNoImage]: isOwnListing,
                  })}
                >
                  <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                </H3>
              )}
            </div>
            {saveCountBySeller > 0 ? (
              <div className={css.pdpCartMessage}>
                <IconBagFilled />
                <p>
                  <FormattedMessage
                    id={
                      saveCountBySeller > 1
                        ? 'ListingPage.saveCountBySellerPeople'
                        : 'ListingPage.saveCountBySeller'
                    }
                    values={{ saveCountBySeller }}
                  />
                </p>
              </div>
            ) : null}
            {isVacationMode ? (
              <p className={css.vacationModeNotice} role="status">
                <FormattedMessage id="ListingPage.sellerVacationModeUnavailable" />
              </p>
            ) : null}
            <SectionTextMaybe text={description} showAsIngress />

            <CustomListingFields
              publicData={publicData}
              metadata={metadata}
              listingFieldConfigs={listingConfig.listingFields}
              categoryConfiguration={config.categoryConfiguration}
              intl={intl}
            />

            <SectionMapMaybe
              geolocation={geolocation}
              publicData={publicData}
              listingId={activeListing.id}
              mapsConfig={config.maps}
            />
            <SectionReviews reviews={reviews} fetchReviewsError={fetchReviewsError} />
            <SectionTags
              publicData={publicData}
              history={history}
              routeConfiguration={routeConfiguration}
            />
            <SectionAuthorMaybe
              title={title}
              listing={activeListing}
              authorDisplayName={authorDisplayName}
              onContactUser={onContactUser}
              isInquiryModalOpen={isAuthenticated && inquiryModalOpen}
              onCloseInquiryModal={() => {
                setInquiryModalOpen(false);
                setInquiryType('inquiry');
                setPhoneVerificationRequired(false);
                setPhoneVerificationSuccess(false);
              }}
              sendInquiryError={sendInquiryError}
              sendInquiryInProgress={sendInquiryInProgress}
              onSubmitInquiry={onSubmitInquiry}
              currentUser={currentUser}
              phoneVerificationRequired={phoneVerificationRequired}
              phoneVerificationSuccess={phoneVerificationSuccess}
              onPhoneVerified={() => {
                setPhoneVerificationRequired(false);
                setPhoneVerificationSuccess(true);
              }}
              onRefreshCurrentUser={onRefreshCurrentUser}
              onManageDisableScrolling={onManageDisableScrolling}
              inquiryType={inquiryType}
            />
          </div>
          <div className={css.orderColumnForHeroLayout}>
            <OrderPanel
              className={css.orderPanel}
              listing={activeListing}
              isOwnListing={isOwnListing}
              onSubmit={handleOrderSubmit}
              purchaseCtaMessageId={purchaseCtaMessageId}
              purchaseFormSubmitMessageId={purchaseCtaMessageId}
              purchaseCtaOnClick={isPurchase && isInCart ? handleRemoveFromCart : null}
              purchaseFormSkipRequiredValidation={isPurchase && isInCart}
              purchaseCtaDisabled={
                isVacationMode || (isPurchase && isActiveListingCartActionPending)
              }
              purchaseFormSubmitDisabled={
                isVacationMode || (isPurchase && isActiveListingCartActionPending)
              }
              selectedSize={selectedSizeOption?.values || ''}
              selectedVariantListingId={activeListingId}
              onSelectSize={value => setSelectedSize(value || '')}
              authorLink={
                <NamedLink
                  className={css.authorNameLink}
                  name={isVariant ? 'ListingPageVariant' : 'ListingPage'}
                  params={params}
                  to={{ hash: '#author' }}
                >
                  {authorDisplayName}
                </NamedLink>
              }
              title={<FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />}
              titleDesktop={
                <H4 as="h1" className={css.orderPanelTitle}>
                  <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                </H4>
              }
              payoutDetailsWarning={payoutDetailsWarning}
              author={ensuredAuthor}
              onManageDisableScrolling={onManageDisableScrolling}
              onContactUser={canTransactOnActiveListing && !isVacationMode ? onContactUser : null}
              {...restOfProps}
              validListingTypes={config.listing.listingTypes}
              marketplaceCurrency={config.currency}
              dayCountAvailableForBooking={config.stripe.dayCountAvailableForBooking}
              marketplaceName={config.marketplaceName}
              showListingImage={showListingImage}
            />
          </div>
        </div>
        {hasSimilarFilters ? (
          <div className={css.similarListingsObserver} ref={similarListingsObserverRef} />
        ) : null}
        {showSimilarListingsSection ? (
          <div className={classNames(css.contentWrapperForHeroLayout, css.similarListingsWrapper)}>
            <div className={css.similarListingsSection}>
              <h2 className={css.similarListingsTitle}>
                <FormattedMessage
                  id="ListingPage.similarListings"
                  defaultMessage="Similar Listings"
                />
              </h2>
              {fetchSimilarListingsInProgress ? (
                <div className={css.similarListingsLoading}>
                  <IconSpinner />
                </div>
              ) : (
                <div className={css.similarListingsGrid}>
                  {similarListings.map(listing => (
                    <ListingCard
                      key={listing?.id?.uuid}
                      className={css.similarListingCard}
                      listing={listing}
                      intl={intl}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : null}
      </LayoutSingleColumn>
    </Page>
  );
};

/**
 * The ListingPage component with cover photo layout
 *
 * @component
 * @param {Object} props
 * @param {Object} props.params - The path params object
 * @param {string} props.params.id - The listing id
 * @param {string} props.params.slug - The listing slug
 * @param {LISTING_PAGE_DRAFT_VARIANT | LISTING_PAGE_PENDING_APPROVAL_VARIANT} props.params.variant - The listing variant
 * @param {boolean} props.isAuthenticated - Whether the user is authenticated
 * @param {Object} props.currentUser - The current user
 * @param {Function} props.getListing - The get listing function
 * @param {Function} props.getOwnListing - The get own listing function
 * @param {Function} props.onManageDisableScrolling - The on manage disable scrolling function
 * @param {boolean} props.scrollingDisabled - Whether scrolling is disabled
 * @param {string} props.inquiryModalOpenForListingId - The inquiry modal open for the specific listing id
 * @param {propTypes.error} props.showListingError - The show listing error
 * @param {Function} props.callSetInitialValues - The call setInitialValues function, which is given to this function as a parameter
 * @param {Array<propTypes.review>} props.reviews - The reviews
 * @param {propTypes.error} props.fetchReviewsError - The fetch reviews error
 * @param {Object<string, Object>} props.monthlyTimeSlots - The monthly time slots. E.g. { '2019-11': { timeSlots: [], fetchTimeSlotsInProgress: false, fetchTimeSlotsError: null } }
 * @param {Object<string, Object>} props.timeSlotsForDate - The time slots for date. E.g. { '2019-11-01': { timeSlots: [], fetchedAt: 1572566400000, fetchTimeSlotsError: null, fetchTimeSlotsInProgress: false } }
 * @param {boolean} props.sendInquiryInProgress - Whether the send inquiry is in progress
 * @param {propTypes.error} props.sendInquiryError - The send inquiry error
 * @param {Function} props.onSendInquiry - The on send inquiry function
 * @param {Function} props.onInitializeCardPaymentData - The on initialize card payment data function
 * @param {Function} props.onFetchTimeSlots - The on fetch time slots function
 * @param {Function} props.onFetchTransactionLineItems - The on fetch transaction line items function
 * @param {Array<propTypes.transactionLineItem>} props.lineItems - The line items
 * @param {boolean} props.fetchLineItemsInProgress - Whether the fetch line items is in progress
 * @param {propTypes.error} props.fetchLineItemsError - The fetch line items error

 * @returns {JSX.Element} listing page component
 */
const EnhancedListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const showListingError = props.showListingError;
  const isVariant = props.params?.variant != null;
  const currentUser = props.currentUser;
  if (isForbiddenError(showListingError) && !isVariant && !currentUser) {
    // This can happen if private marketplace mode is active
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRights = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(showListingError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (hasNoViewingRights && isForbiddenError(showListingError)) ||
    isErrorNoViewingPermission(showListingError)
  ) {
    // If the user has no viewing rights, fetching anything but their own listings
    // will return a 403 error. If that happens, redirect to NoAccessPage.
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <ListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      showOwnListingsOnly={hasNoViewingRights}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    timeSlotsForDate,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
    variantListingsIds,
    similarListingsIds,
    fetchSimilarListingsInProgress,
    saveCountBySeller,
  } = state.ListingPage;
  const { currentUser } = state.user;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };
  const viewerUserId = currentUser?.id?.uuid;
  const variantListings = filterPublicListings(
    variantListingsIds?.length ? getListingsById(state, variantListingsIds) : [],
    viewerUserId
  );
  const similarListings = filterPublicListings(
    similarListingsIds?.length ? getListingsById(state, similarListingsIds) : [],
    viewerUserId
  );

  return {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    inquiryModalOpenForListingId,
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots, // for OrderPanel
    timeSlotsForDate, // for OrderPanel
    lineItems, // for OrderPanel
    fetchLineItemsInProgress, // for OrderPanel
    fetchLineItemsError, // for OrderPanel
    sendInquiryInProgress,
    sendInquiryError,
    variantListings,
    similarListings,
    fetchSimilarListingsInProgress,
    saveCountBySeller,
    cartItems: state?.cart?.items || {},
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: params => dispatch(fetchTransactionLineItems(params)), // for OrderPanel
  onSendInquiry: (listing, message, offerParams) =>
    dispatch(sendInquiry(listing, message, offerParams)),
  onRefreshCurrentUser: () =>
    dispatch(
      fetchCurrentUser({
        enforce: true,
        updateHasListings: false,
        updateNotifications: false,
      })
    ),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onAddToCart: ({
    listingId,
    quantity = 1,
    sellerId = null,
    maxQuantity = null,
    selectedSize = null,
    selectedSizeLabel = null,
  }) =>
    dispatch(
      addItemToCart({
        listingId,
        quantity,
        sellerId,
        maxQuantity,
        selectedSize,
        selectedSizeLabel,
      })
    ),
  onRemoveFromCart: listingId => dispatch(removeItemFromCart({ listingId })),
  onFetchTimeSlots: (listingId, start, end, timeZone, options) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone, options)), // for OrderPanel
  onFetchSimilarListings: params => dispatch(fetchSimilarListings(params)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(EnhancedListingPage);

export default ListingPage;
